/**
 * analytics.js — lightweight, privacy-safe product analytics.
 *
 * Philosophy:
 *   - Zero PII: no names, emails, content, or UUIDs of pastoral records
 *   - Route patterns only (no IDs): "/churches/:id" → "churches_detail"
 *   - Event names only: what the user DID, not what they entered
 *   - All events flow through crashLogger.info() → Sentry breadcrumbs
 *     (or any reporter wired via crashLogger.setReporter)
 *
 * Usage:
 *   import { analytics } from './lib/analytics';
 *   analytics.screen('churches_list');
 *   analytics.event('create_family');
 *   analytics.event('sync_manual');
 */

import { crashLogger } from './crash-logger';

// Map route patterns to readable screen names (IDs stripped)
const ROUTE_MAP = [
  [/^\/$/, 'dashboard'],
  [/^\/churches$/, 'churches_list'],
  [/^\/churches\/[^/]+$/, 'church_detail'],
  [/^\/churches\/[^/]+\/edit/, 'church_edit'],
  [/^\/churches\/new/, 'church_new'],
  [/^\/families$/, 'families_list'],
  [/^\/families\/[^/]+$/, 'family_detail'],
  [/^\/families\/[^/]+\/edit/, 'family_edit'],
  [/^\/families\/new/, 'family_new'],
  [/^\/people\/[^/]+$/, 'person_detail'],
  [/^\/people\/[^/]+\/edit/, 'person_edit'],
  [/^\/visits$/, 'visits_list'],
  [/^\/visits\/new/, 'visit_new'],
  [/^\/visits\/[^/]+$/, 'visit_detail'],
  [/^\/prayers$/, 'prayers_list'],
  [/^\/prayers\/new/, 'prayer_new'],
  [/^\/agenda/, 'agenda'],
  [/^\/district/, 'district'],
  [/^\/notes$/, 'notes_list'],
  [/^\/notes\/new/, 'note_new'],
  [/^\/notes\/[^/]+/, 'note_detail'],
  [/^\/settings/, 'settings'],
  [/^\/profile/, 'profile'],
  [/^\/preaching$/, 'preaching_list'],
  [/^\/preaching\/new/, 'preaching_new'],
  [/^\/preaching\/[^/]+/, 'preaching_detail'],
  [/^\/admin\/users/, 'admin_users'],
  [/^\/search/, 'search'],
];

function resolveScreen(pathname) {
  for (const [pattern, name] of ROUTE_MAP) {
    if (pattern.test(pathname)) return name;
  }
  return 'unknown';
}

let _sessionStart = Date.now();
let _lastScreen = null;

export const analytics = {
  /**
   * Track a screen view. Called automatically by useAnalytics hook.
   * @param {string} screen  from ROUTE_MAP or custom
   */
  screen(screen) {
    if (screen === _lastScreen) return;
    _lastScreen = screen;
    crashLogger.info('screen_view', { screen });
  },

  /**
   * Track a user action. No content or IDs — event name only.
   * @param {string} event  e.g. 'create_family', 'sync_manual', 'answer_prayer'
   * @param {object} [dims] optional non-PII dimensions (e.g. { has_church: true })
   */
  event(event, dims = {}) {
    crashLogger.info('user_event', { event, ...dims });
  },

  /**
   * Track session start. Call once when user is authenticated.
   */
  sessionStart() {
    _sessionStart = Date.now();
    crashLogger.info('session_start', {});
  },

  /**
   * Track session end / app backgrounded.
   */
  sessionEnd() {
    const duration_s = Math.round((Date.now() - _sessionStart) / 1000);
    crashLogger.info('session_end', { duration_s });
  },

  /**
   * Resolve a pathname to a screen name without tracking.
   */
  resolve: resolveScreen,
};

// useAnalytics is exported from hooks/useAnalytics.js to keep this module React-free.
